import React from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiUser, FiBookOpen, FiUsers } = FiIcons;

    const TutorStudentsModal = ({ tutor, onClose }) => {
      const { students, getParentById, formatCurrency } = useData();

      // Ensure we have a name string to match against
      const tutorNameString = tutor.name || `${tutor.firstName} ${tutor.lastName}`.trim();

      // Robust matching logic reused from TutorManagement
      const isTutorAssigned = (student) => {
        const tutorFullName = tutorNameString.toLowerCase();
        const tutorFirstName = (tutor.firstName || '').toLowerCase();
        const tutorLastName = (tutor.lastName || '').toLowerCase();
        
        const checkMatch = (tutorStr) => {
          if (!tutorStr) return false;
          const str = tutorStr.toLowerCase().trim();
          return (
            str === tutorFullName || 
            str === tutorFirstName || 
            (tutorLastName && str.includes(tutorLastName) && str.includes(tutorFirstName))
          );
        };

        if (student.subjects && Array.isArray(student.subjects)) {
          return student.subjects.some(sub => checkMatch(sub.tutor));
        }
        return checkMatch(student.tutor);
      };

      const myStudents = students.filter(student => isTutorAssigned(student));

      const getStatusColor = (status) => {
        const colors = {
          'Active': 'bg-green-100 text-green-800',
          'Trial': 'bg-yellow-100 text-yellow-800',
          'Idle': 'bg-gray-100 text-gray-800',
          'Hold': 'bg-orange-100 text-orange-800',
          'Finished': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
      };

      // Filter subjects for display to show only ones relevant to this tutor
      const getRelevantSubjects = (student) => {
        const tutorFullName = tutorNameString.toLowerCase();
        const tutorFirstName = (tutor.firstName || '').toLowerCase();
        
        if (student.subjects && Array.isArray(student.subjects)) {
          return student.subjects.filter(sub => {
            if (!sub.tutor) return false;
            const str = sub.tutor.toLowerCase().trim();
            return str === tutorFullName || str === tutorFirstName || str.includes(tutorFirstName);
          });
        }
        // Legacy fallback
        if (student.tutor && (student.tutor.toLowerCase().includes(tutorFirstName))) {
          return [{ subject: student.subject, feePerSession: student.feePerSession }];
        }
        return [];
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            exit={{ opacity: 0, scale: 0.95 }} 
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Students Assigned to {tutorNameString}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {myStudents.length} assigned students
                </p>
              </div>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="p-6">
              {myStudents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Parent
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subjects Taught by You
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {myStudents.map((student) => {
                        const parent = getParentById(student.parentId);
                        const relevantSubjects = getRelevantSubjects(student);

                        return (
                          <tr key={student.id}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{student.studentName}</div>
                              <div className="text-sm text-gray-500">Grade {student.grade}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {parent ? (
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{parent.firstName} {parent.lastName}</div>
                                  <div className="text-sm text-gray-500">{parent.phone}</div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">Not assigned</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-1">
                                {relevantSubjects.length > 0 ? relevantSubjects.map((sub, idx) => (
                                  <div key={idx} className="text-sm text-gray-900">
                                    {sub.subject} ({formatCurrency(parseFloat(sub.feePerSession || 0))})
                                  </div>
                                )) : (
                                  <span className="text-sm text-gray-400 italic">General / Unspecified</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(student.status)}`}>
                                {student.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <SafeIcon icon={FiUsers} className="text-gray-400 text-4xl mx-auto mb-4" />
                  <p className="text-gray-500">This tutor has no assigned students found.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button 
                onClick={onClose} 
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      );
    };

    export default TutorStudentsModal;